import { EnhancedAssumptions, EnhancedScenarioResults } from '../types';

// Tax constants
const FEDERAL_DEPRECIATION_RECAPTURE_RATE = 0.25; // IRS Section 1250
const CA_STATE_TAX_RATE = 0.123; // CA top marginal rate
const PROP_13_MAX_GROWTH_RATE = 0.02; // 2% nominal max assessed value growth
const SP500_DIVIDEND_YIELD = 0.013; // ~1.3% dividend yield

export const calculateScenarios = (assumptions: EnhancedAssumptions): EnhancedScenarioResults[] => {
  const results: EnhancedScenarioResults[] = [];
  const initialSaleProceeds = assumptions.propertyValue * (1 - assumptions.sellingCosts / 100);

  // Depreciation calculation (27.5 years for residential)
  // For coastal Newport Beach, land is ~75% of value, building is ~25%
  const buildingValue = assumptions.propertyValue * 0.25;
  const annualDepreciation = buildingValue / 27.5;

  // Step-up basis is the property value at inheritance (assumed to be current value)
  const stepUpBasis = assumptions.propertyValue;

  // Tax drag calculation for portfolio returns (applies to both sell and rental reinvestment)
  // Dividends taxed at capital gains rate, bond interest at ordinary income rate
  const dividendTaxRate = assumptions.capitalGainsRate / 100;
  const bondInterestTaxRate = assumptions.rentalIncomeTaxRate / 100;

  // Effective drag per year (applied to returns)
  // Stocks: 1.3% dividend yield * tax rate = ~0.4-0.5% drag on the 80% equity portion
  // Bonds: ~4% yield * tax rate = ~1.2-1.8% drag on the 20% bond portion
  const equityDividendDrag = SP500_DIVIDEND_YIELD * dividendTaxRate; // Applied to equity portion
  const bondInterestRate = 0.04; // Assume ~4% bond yield
  const bondInterestDrag = bondInterestRate * bondInterestTaxRate; // Applied to bond portion

  // Calculate after-tax-drag blended return (used for both rental reinvestment and sell scenario)
  const effectiveStockReturn = assumptions.stockReturns - (equityDividendDrag * 100);
  const effectiveBondReturn = assumptions.bondReturns - (bondInterestDrag * 100);
  const blendedReturn = (0.8 * effectiveStockReturn) + (0.2 * effectiveBondReturn);

  let cumulativeSpentIncome = 0;
  let investedRentalPortfolio = 0;
  let cumulativeTaxDrag = 0;

  // Track assessed value separately (Prop 13 caps growth at 2%/year after reassessment)
  // At year 0, property is reassessed to market value due to Prop 19
  let assessedValue = assumptions.propertyValue; // Reassessed at inheritance

  for (let year = 0; year <= 10; year++) {
    // Property calculations
    const currentPropertyValue = assumptions.propertyValue * Math.pow(1 + assumptions.propertyAppreciation / 100, year);
    const currentAnnualRent = assumptions.monthlyRent * 12 * Math.pow(1 + assumptions.rentGrowth / 100, year);

    // Prop 13: Assessed value grows at max 2%/year nominal (not tracking market value)
    // Convert real appreciation to nominal for comparison with 2% cap
    const nominalAppreciation = assumptions.propertyAppreciation / 100 + assumptions.inflationRate / 100;
    const assessedGrowthRate = Math.min(nominalAppreciation, PROP_13_MAX_GROWTH_RATE);
    if (year > 0) {
      assessedValue = assumptions.propertyValue * Math.pow(1 + assessedGrowthRate, year);
    }

    // Property tax based on ASSESSED value (not market value) - this is the Prop 13 fix
    const propertyTax = assessedValue * assumptions.propertyTaxRate / 100;

    // Other expenses (still based on market value where appropriate)
    const insurance = assumptions.insuranceCost;
    const maintenance = currentPropertyValue * assumptions.maintenanceRate / 100;
    const vacancyLoss = currentAnnualRent * assumptions.vacancyRate / 100;
    const managementCost = (currentAnnualRent - vacancyLoss) * assumptions.managementFee / 100;
    const totalExpenses = propertyTax + insurance + maintenance + vacancyLoss + managementCost;

    const grossRentalIncome = currentAnnualRent - vacancyLoss;
    const netRentalIncome = grossRentalIncome - (propertyTax + insurance + maintenance + managementCost);

    // Tax calculations for rental income
    const depreciationDeduction = assumptions.depreciation ? annualDepreciation : 0;
    const taxableRentalIncome = Math.max(0, netRentalIncome - depreciationDeduction);
    const rentalIncomeTax = taxableRentalIncome * assumptions.rentalIncomeTaxRate / 100;
    const netRentalIncomeAfterTax = netRentalIncome - rentalIncomeTax;

    // Handle spending vs investing rental income
    // Use blended return (same as sell scenario) for consistency
    if (year > 0) {
      investedRentalPortfolio *= (1 + blendedReturn / 100);
    }

    if (year > 0) {
      if (assumptions.rentalIncomeStrategy === 'spend') {
        cumulativeSpentIncome += netRentalIncomeAfterTax;
      } else {
        investedRentalPortfolio += netRentalIncomeAfterTax;
      }
    }

    // Cumulative calculations for reporting
    let cumulativeNetIncome = -assumptions.rentalSetupCosts;
    let cumulativeDepreciation = 0;

    for (let y = 1; y <= year; y++) {
      const yearRent = assumptions.monthlyRent * 12 * Math.pow(1 + assumptions.rentGrowth / 100, y);
      const yearAssessedValue = assumptions.propertyValue * Math.pow(1 + assessedGrowthRate, y);
      const yearPropertyTax = yearAssessedValue * assumptions.propertyTaxRate / 100;
      const yearPropertyValue = assumptions.propertyValue * Math.pow(1 + assumptions.propertyAppreciation / 100, y);
      const yearExpenses =
        yearPropertyTax +
        assumptions.insuranceCost +
        (yearPropertyValue * assumptions.maintenanceRate / 100) +
        (yearRent * assumptions.vacancyRate / 100) +
        ((yearRent - yearRent * assumptions.vacancyRate / 100) * assumptions.managementFee / 100);
      const yearNetRental = yearRent - yearExpenses;
      const yearTaxableIncome = Math.max(0, yearNetRental - annualDepreciation);
      const yearTax = yearTaxableIncome * assumptions.rentalIncomeTaxRate / 100;
      cumulativeNetIncome += yearNetRental - yearTax;
      cumulativeDepreciation += annualDepreciation;
    }

    // Calculate gross equity (before exit costs/taxes)
    let totalEquity;
    if (assumptions.rentalIncomeStrategy === 'spend') {
      totalEquity = currentPropertyValue - assumptions.rentalSetupCosts;
    } else {
      totalEquity = currentPropertyValue - assumptions.rentalSetupCosts + investedRentalPortfolio;
    }

    // Exit tax calculations for rental scenario (if sold at this year)
    // 1. Depreciation recapture: 25% federal + CA state rate on cumulative depreciation
    const depreciationRecaptureTax = cumulativeDepreciation *
      (FEDERAL_DEPRECIATION_RECAPTURE_RATE + CA_STATE_TAX_RATE);

    // 2. Capital gains on post-step-up appreciation
    const appreciationSinceInheritance = currentPropertyValue - stepUpBasis;
    const capitalGainsTax = appreciationSinceInheritance > 0
      ? appreciationSinceInheritance * (assumptions.capitalGainsRate / 100)
      : 0;

    // 3. Selling costs at exit
    const sellingCostsAtExit = currentPropertyValue * (assumptions.sellingCosts / 100);

    // 4. Net liquidation value (what you actually walk away with)
    const netLiquidationValueRent = totalEquity - depreciationRecaptureTax - capitalGainsTax - sellingCostsAtExit;

    // Investment scenarios with tax drag
    const stockReturn = assumptions.stockReturns;
    const bondReturn = assumptions.bondReturns;

    // Calculate after-tax returns (accounting for dividend/interest tax drag)
    // The drag is applied annually, reducing effective returns
    const effectiveStockReturn = stockReturn - (equityDividendDrag * 100);
    const effectiveBondReturn = bondReturn - (bondInterestDrag * 100);

    const portfolio100Return = effectiveStockReturn;
    const portfolio80_20Return = (0.8 * effectiveStockReturn) + (0.2 * effectiveBondReturn);
    const portfolio60_40Return = (0.6 * effectiveStockReturn) + (0.4 * effectiveBondReturn);

    const investment100Value = initialSaleProceeds * Math.pow(1 + portfolio100Return / 100, year);
    const investment80_20Value = initialSaleProceeds * Math.pow(1 + portfolio80_20Return / 100, year);
    const investment60_40Value = initialSaleProceeds * Math.pow(1 + portfolio60_40Return / 100, year);

    // Track cumulative tax drag paid over time
    if (year > 0) {
      const prevValue80_20 = initialSaleProceeds * Math.pow(1 + portfolio80_20Return / 100, year - 1);
      const yearDrag80_20 = prevValue80_20 * (
        0.8 * equityDividendDrag + 0.2 * bondInterestDrag
      );
      cumulativeTaxDrag += yearDrag80_20;
    }

    // Net liquidation values for sell scenarios (if fully sold)
    // Capital gains would be: portfolio value - cost basis (initial sale proceeds)
    const calcNetLiquidation = (portfolioValue: number): number => {
      const gain = portfolioValue - initialSaleProceeds;
      const capGainsTax = gain > 0 ? gain * (assumptions.capitalGainsRate / 100) : 0;
      return portfolioValue - capGainsTax;
    };

    const inflationAdjustment = 1; // Already in real terms

    results.push({
      year: year + 2025,
      rentScenario: {
        propertyValue: currentPropertyValue,
        propertyValueReal: currentPropertyValue / inflationAdjustment,
        assessedValue,
        annualRent: currentAnnualRent,
        annualRentReal: currentAnnualRent / inflationAdjustment,
        grossRentalIncome,
        totalExpenses,
        propertyTax,
        netRentalIncome,
        netRentalIncomeAfterTax,
        cumulativeNetIncome,
        cumulativeSpentIncome,
        cumulativeReinvestedIncome: investedRentalPortfolio,
        totalEquity,
        totalEquityReal: totalEquity / inflationAdjustment,
        depreciationDeduction,
        cumulativeDepreciation,
        depreciationRecaptureTax,
        capitalGainsTax,
        sellingCostsAtExit,
        netLiquidationValue: netLiquidationValueRent
      },
      sellScenarios: {
        saleProceeds: year === 0 ? initialSaleProceeds : 0,
        saleProceedsReal: year === 0 ? initialSaleProceeds : 0,
        cumulativeTaxDrag,
        portfolio100: {
          investmentValue: investment100Value,
          investmentValueReal: investment100Value / inflationAdjustment,
          totalValue: investment100Value,
          totalValueReal: investment100Value / inflationAdjustment,
          netLiquidationValue: calcNetLiquidation(investment100Value)
        },
        portfolio80_20: {
          investmentValue: investment80_20Value,
          investmentValueReal: investment80_20Value / inflationAdjustment,
          totalValue: investment80_20Value,
          totalValueReal: investment80_20Value / inflationAdjustment,
          netLiquidationValue: calcNetLiquidation(investment80_20Value)
        },
        portfolio60_40: {
          investmentValue: investment60_40Value,
          investmentValueReal: investment60_40Value / inflationAdjustment,
          totalValue: investment60_40Value,
          totalValueReal: investment60_40Value / inflationAdjustment,
          netLiquidationValue: calcNetLiquidation(investment60_40Value)
        }
      }
    });
  }

  return results;
};
